import { configureStore } from '@reduxjs/toolkit'
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux'
import authReducer from './features/auth/authSlice'
import maintenanceReducer from './features/maintenance/maintenanceSlice'
import companyReducer from './features/company/companySlice'
import userReducer from './features/user/userSlice'
import uiReducer from './features/ui/uiSlice'

/**
 * Central Redux store for the serviceman application.
 *
 * Combines 5 feature slices:
 * - `auth` — PIN-based authentication, JWT token, feature flags
 * - `maintenance` — Maintenance request CRUD, filters, stats, detail view
 * - `company` — Company list, selection, technical details
 * - `user` — User profile/settings (email, username)
 * - `ui` — UI state (sidebar, theme, notifications)
 *
 * @remarks
 * Serializable check ignores `auth/setToken` to allow direct token string setting.
 * DevTools are disabled in production builds.
 * Always use the typed hooks `useAppDispatch` and `useAppSelector` instead of
 * raw `useDispatch`/`useSelector` for proper TypeScript inference.
 */
export const store = configureStore({
  reducer: {
    auth: authReducer,
    maintenance: maintenanceReducer,
    company: companyReducer,
    user: userReducer,
    ui: uiReducer,
  },
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types for serializability check
        ignoredActions: ['auth/setToken'],
      },
    }),
  devTools: process.env.NODE_ENV !== 'production',
})

/** Root state type derived from the store's reducer configuration. */
export type RootState = ReturnType<typeof store.getState>
/** Typed dispatch that supports thunk actions. */
export type AppDispatch = typeof store.dispatch

/**
 * Typed `useDispatch` hook — use this instead of raw `useDispatch`.
 * Properly types thunk dispatch return values.
 */
export const useAppDispatch = () => useDispatch<AppDispatch>()
/**
 * Typed `useSelector` hook — use this instead of raw `useSelector`.
 * Provides automatic `RootState` typing for selector functions.
 */
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector
